import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Bell, BellOff, TrendingDown, FileText, PenTool, Clock, Shield,
  Check, X, Save, Loader2
} from 'lucide-react';

const NotificationSettingsPanel = ({ onClose }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    risk_improvement: true,
    contract_updates: true,
    signature_requests: true,
    deadlines: true,
    legal_updates: true,
    minimum_improvement_threshold: 20
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/push/notification-settings');
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
      setPushEnabled(response.data.push_enabled);
      setEmailEnabled(response.data.email_enabled);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/push/notification-settings', settings);
      toast.success('Preferencias guardadas');
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const notificationTypes = [
    {
      key: 'risk_improvement',
      icon: TrendingDown,
      title: 'Mejora de Riesgo',
      description: 'Cuando tu contrato reduce su puntuación de riesgo',
      color: 'text-green-400'
    },
    {
      key: 'contract_updates',
      icon: FileText,
      title: 'Actualizaciones de Contratos',
      description: 'Cambios en tus contratos o nuevas versiones',
      color: 'text-blue-400'
    },
    {
      key: 'signature_requests',
      icon: PenTool,
      title: 'Solicitudes de Firma',
      description: 'Cuando alguien te solicita firmar un documento',
      color: 'text-purple-400'
    },
    {
      key: 'deadlines',
      icon: Clock,
      title: 'Vencimientos',
      description: 'Recordatorios de fechas límite importantes',
      color: 'text-orange-400'
    },
    {
      key: 'legal_updates',
      icon: Shield,
      title: 'Actualizaciones Legales',
      description: 'Cambios en la legislación que te afectan',
      color: 'text-cyan-400'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Bell className="w-5 h-5 text-purple-400" />
          Preferencias de Notificaciones
        </h3>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Notification Status */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`p-4 rounded-xl border ${pushEnabled ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
          <div className="flex items-center gap-2 mb-1">
            {pushEnabled ? <Bell className="w-4 h-4 text-green-400" /> : <BellOff className="w-4 h-4 text-slate-500" />}
            <span className={`font-medium ${pushEnabled ? 'text-green-400' : 'text-slate-400'}`}>
              Push
            </span>
          </div>
          <span className="text-xs text-slate-500">
            {pushEnabled ? 'Activo' : 'No configurado'}
          </span>
        </div>
        <div className={`p-4 rounded-xl border ${emailEnabled ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
          <div className="flex items-center gap-2 mb-1">
            {emailEnabled ? <Check className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-slate-500" />}
            <span className={`font-medium ${emailEnabled ? 'text-green-400' : 'text-slate-400'}`}>
              Email
            </span>
          </div>
          <span className="text-xs text-slate-500">
            {emailEnabled ? 'Activo' : 'Desactivado'}
          </span>
        </div>
      </div>

      {/* Notification Types */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-400">Tipos de Notificaciones</h4>
        {notificationTypes.map((type) => {
          const IconComponent = type.icon;
          const isEnabled = settings[type.key];
          
          return (
            <div
              key={type.key}
              onClick={() => toggleSetting(type.key)}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                isEnabled 
                  ? 'bg-slate-800/50 border-slate-600 hover:border-slate-500' 
                  : 'bg-slate-900/50 border-slate-700 opacity-60 hover:opacity-80'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isEnabled ? 'bg-slate-700' : 'bg-slate-800'}`}>
                    <IconComponent className={`w-5 h-5 ${isEnabled ? type.color : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <div className="font-medium">{type.title}</div>
                    <div className="text-xs text-slate-500">{type.description}</div>
                  </div>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors ${isEnabled ? 'bg-purple-600' : 'bg-slate-700'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${isEnabled ? 'translate-x-6 ml-0.5' : 'translate-x-0.5'}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Threshold Setting for Risk Improvement */}
      {settings.risk_improvement && (
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
          <h4 className="text-sm font-medium mb-3">Umbral de Mejora para Notificación</h4>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="10"
              max="50"
              step="5"
              value={settings.minimum_improvement_threshold}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                minimum_improvement_threshold: parseInt(e.target.value)
              }))}
              className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="w-16 text-center px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 font-bold">
              {settings.minimum_improvement_threshold}%
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Solo recibirás notificaciones cuando el riesgo mejore al menos {settings.minimum_improvement_threshold}%
          </p>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
      >
        {saving ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Save className="w-5 h-5" />
        )}
        Guardar Preferencias
      </button>
    </motion.div>
  );
};

export default NotificationSettingsPanel;
